import React, { useEffect, useRef, useState } from 'react';
import Icon from '../../../components/AppIcon';

const ProblemSection = () => {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef?.current) {
      observer?.observe(sectionRef?.current);
    }

    return () => observer?.disconnect();
  }, []);

  const problems = [
    {
      icon: 'Layers',
      title: 'Разрозненные системы',
      description: 'CRM, складской учет, финансы, аналитика - все в разных программах',
      metric: '5-8 систем',
      color: 'error'
    },
    {
      icon: 'Database',
      title: 'Потеря данных',
      description: 'Информация теряется при переносе между системами',
      metric: '30% данных',
      color: 'warning'
    },
    {
      icon: 'Clock',
      title: 'Ручная работа',
      description: 'Сотрудники тратят часы на рутинные операции',
      metric: '4 часа/день',
      color: 'secondary'
    },
    {
      icon: 'TrendingDown',
      title: 'Упущенные продажи',
      description: 'Лиды теряются из-за медленной обработки',
      metric: '25% лидов',
      color: 'destructive'
    }
  ];

  return (
    <section ref={sectionRef} className="py-24 relative overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-b from-background via-card to-background" />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className={`text-center mb-16 transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <h2 className="text-4xl sm:text-5xl font-black text-foreground mb-4">
            Знакомая ситуация?
          </h2>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
            Типичные проблемы предприятий с устаревшей инфраструктурой
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {problems?.map((problem, index) => (
            <div
              key={index}
              className={`glassmorphism rounded-xl p-6 hover:scale-105 transition-all duration-500 ${
                isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
              }`}
              style={{ transitionDelay: `${index * 100}ms` }}
            >
              <div className={`w-12 h-12 rounded-lg bg-${problem?.color}/10 flex items-center justify-center mb-4`}>
                <Icon name={problem?.icon} size={24} color={`var(--color-${problem?.color})`} />
              </div>
              
              <h3 className="text-lg font-bold text-foreground mb-2">
                {problem?.title}
              </h3>
              
              <p className="text-sm text-muted-foreground mb-4">
                {problem?.description}
              </p>
              
              <div className={`inline-flex items-center space-x-2 px-3 py-1.5 rounded-full bg-${problem?.color}/10 border border-${problem?.color}/20`}>
                <Icon name="AlertCircle" size={14} color={`var(--color-${problem?.color})`} />
                <span className={`text-xs font-bold text-${problem?.color}`}>
                  {problem?.metric}
                </span>
              </div>
            </div>
          ))}
        </div>

        <div className={`mt-16 glassmorphism-strong rounded-2xl p-8 text-center transition-all duration-1000 delay-500 ${
          isVisible ? 'opacity-100 scale-100' : 'opacity-0 scale-95'
        }`}>
          <div className="flex items-center justify-center space-x-3 mb-4">
            <Icon name="TrendingDown" size={32} color="var(--color-error)" />
            <h3 className="text-2xl font-bold text-foreground">
              Результат: потеря до 40% потенциальной прибыли
            </h3>
          </div>
          <p className="text-muted-foreground max-w-2xl mx-auto">
            Предприятия теряют миллионы рублей ежегодно из-за неэффективных процессов и устаревших систем
          </p>
        </div>
      </div>
    </section>
  );
};

export default ProblemSection;